import { create } from 'zustand'

import { CLIENT_ID } from '@/api/client'
import { createMemo, deleteMemo, getMemos, type MemoListItem, renameMemo, updateMemo } from '@/api/services/memo'
import { registerSyncListener } from '@/api/sync-events'

type MemoState = {
  memos: MemoListItem[]
  isLoaded: boolean
  loadMemos: () => Promise<void>
  addMemo: (title?: string, isCard?: boolean) => Promise<MemoListItem | null>
  renameMemoTitle: (memoId: number, title: string) => Promise<void>
  setMemoCard: (memoId: number, isCard: boolean) => Promise<void>
  removeMemo: (memoId: number) => Promise<void>
}

export const useMemoStore = create<MemoState>((set, get) => ({
  memos: [],
  isLoaded: false,
  loadMemos: async () => {
    try {
      const res = await getMemos()
      set({ memos: res.items, isLoaded: true })
    } catch (error) {
      console.error('Failed to load memos:', error)
      set({ isLoaded: true })
    }
  },
  addMemo: async (title = '', isCard = false) => {
    try {
      const memo = await createMemo(title, isCard)
      const item: MemoListItem = {
        id: memo.id,
        title: memo.title,
        preview: '',
        is_card: memo.is_card,
        created_at: memo.created_at,
        updated_at: memo.updated_at,
      }
      set({ memos: [item, ...get().memos] })
      return item
    } catch (error) {
      console.error('Failed to create memo:', error)
      return null
    }
  },
  renameMemoTitle: async (memoId, title) => {
    try {
      await renameMemo(memoId, title)
      set({
        memos: get().memos.map((m) => (m.id === memoId ? { ...m, title } : m)),
      })
    } catch (error) {
      console.error('Failed to rename memo:', error)
    }
  },
  setMemoCard: async (memoId, isCard) => {
    const apply = (value: boolean) =>
      set({ memos: get().memos.map((m) => (m.id === memoId ? { ...m, is_card: value } : m)) })
    apply(isCard)
    try {
      await updateMemo(memoId, { is_card: isCard })
    } catch (error) {
      console.error('Failed to update memo card flag:', error)
      apply(!isCard)
    }
  },
  removeMemo: async (memoId) => {
    try {
      await deleteMemo(memoId)
      set({ memos: get().memos.filter((m) => m.id !== memoId) })
    } catch (error) {
      console.error('Failed to delete memo:', error)
    }
  },
}))

// ============================================================================
// SSE リモートイベント購読
// ============================================================================
// 他端末でメモが作成/更新/削除されたらリストを再読み込みする。
registerSyncListener((event) => {
  if (event.type !== 'memo.created' && event.type !== 'memo.updated' && event.type !== 'memo.deleted') return
  if (event.origin === CLIENT_ID) return

  const state = useMemoStore.getState()
  if (event.type === 'memo.deleted') {
    useMemoStore.setState({ memos: state.memos.filter((m) => m.id !== event.id) })
    return
  }
  // created / updated は差分計算が難しいので素直に全件再取得
  void state.loadMemos()
})
